import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import {
  SUPPORTED_LANGUAGES,
  localizePath,
  stripLocalePrefix,
  normalizeLanguage,
} from "../../utils/localePaths";

const OG_LOCALES = {
  en: "en_US",
  "pt-BR": "pt_BR",
  es: "es_LA",
  de: "de_DE",
};

// The server serves every route at its trailing-slash form (nginx 301-redirects
// e.g. /features/import → /features/import/). Canonical, og:url and hreflang
// must point at that served URL, otherwise the canonical contradicts the
// redirect target and Google indexes both forms, splitting ranking signals.
// Normalize here so every page is consistent regardless of the value callers
// pass in. URLs that look like files (e.g. .xml) are left untouched.
const withTrailingSlash = (url) => {
  try {
    const u = new URL(url);
    if (!u.pathname.endsWith("/") && !/\.[a-z0-9]+$/i.test(u.pathname)) {
      u.pathname += "/";
    }
    return u.toString();
  } catch {
    return url;
  }
};

export const SeoTags = ({
  title,
  description,
  keywords,
  canonical,
  // Unprefixed, locale-less route path (e.g. "/pricing"). Optional — falls
  // back to stripping any locale prefix off the current URL, which is
  // correct for the vast majority of pages. Only needed when a page can't
  // rely on window.location (none currently), kept as an escape hatch.
  path,
  // Set false for pages whose content isn't translated per-locale (e.g. a
  // single blog post, a user-submitted collection) — skips the hreflang
  // alternate cluster since no translated variant of that URL exists.
  localized = true,
  // `image`/`type` are the canonical prop names; `ogImage`/`ogType` are accepted
  // as aliases so callers can use either convention.
  image,
  ogImage,
  type,
  ogType,
  twitterCard = "summary_large_image",
  siteName = "onBookmarks",
}) => {
  const { i18n } = useTranslation();

  // Append " | onBookmarks" — but only once. Some page title strings already
  // include the suffix, which otherwise produced "… | onBookmarks | onBookmarks".
  const suffix = ` | ${siteName}`;
  const baseTitle = title || siteName;
  const fullTitle =
    baseTitle === siteName || baseTitle.endsWith(suffix)
      ? baseTitle
      : `${baseTitle}${suffix}`;
  const shareTitle = title || siteName;
  const language = normalizeLanguage(i18n.language);
  const origin = window.location.origin;
  const basePath = path || stripLocalePrefix(window.location.pathname).basePath;
  const currentUrl = withTrailingSlash(
    canonical || `${origin}${localizePath(basePath, language)}`
  );

  const resolvedImage = ogImage || image || "/images/og-image.png";
  const resolvedType = ogType || type || "website";
  // Social/AI unfurlers require an absolute og:image/twitter:image URL. A
  // relative path is joined to the current origin; at prerender time that origin
  // is the local server and prerender.js rewrites it to the production origin.
  const absoluteImage = /^https?:\/\//i.test(resolvedImage)
    ? resolvedImage
    : `${window.location.origin}${resolvedImage}`;

  return (
    <Helmet>
      {/* Language Detection */}
      <html lang={language} />
      <meta httpEquiv="Content-Language" content={language} />

      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} key="description" />
      {keywords && <meta name="keywords" content={keywords} key="keywords" />}

      {/* Canonical URL. Each language now has its own prerendered, indexable
          URL (e.g. /es/pricing), so this self-references the current
          language's URL and hreflang below cross-links every variant. */}
      <link rel="canonical" href={currentUrl} />
      {localized &&
        SUPPORTED_LANGUAGES.map((lang) => (
          <link
            rel="alternate"
            hrefLang={lang}
            href={withTrailingSlash(`${origin}${localizePath(basePath, lang)}`)}
            key={lang}
          />
        ))}
      {localized && (
        <link
          rel="alternate"
          hrefLang="x-default"
          href={withTrailingSlash(`${origin}${basePath}`)}
        />
      )}

      {/* Open Graph */}
      <meta property="og:type" content={resolvedType} />
      <meta property="og:title" content={shareTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={currentUrl} />
      <meta property="og:image" content={absoluteImage} />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:locale" content={OG_LOCALES[language] || "en_US"} />
      {Object.entries(OG_LOCALES)
        .filter(([lang]) => lang !== language)
        .map(([lang, locale]) => (
          <meta property="og:locale:alternate" content={locale} key={lang} />
        ))}

      {/* Twitter Card */}
      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:title" content={shareTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={absoluteImage} />

      {/* SEO Tags */}
      <meta name="robots" content="index, follow" />
      <meta name="googlebot" content="index, follow" />
    </Helmet>
  );
};
